import React, { useState, useMemo, useEffect } from 'react';
import { SearchIcon, XIcon, SlidersIcon, ChevronDownIcon, BookOpenIcon } from './icons/InterfaceIcons';
import { supabase } from '../supabase';
import { QuestionCard } from './QuestionCard';

const PAGE_SIZE = 10;

const getExamName = (examId) => {
    const names = {
        'jee-main': 'JEE Main',
        'jee-advanced': 'JEE Advanced',
        'neet': 'NEET',
        'bitsat': 'BITSAT',
        'nda': 'NDA',
        'viteee': 'VITEEE',
    };
    return names[examId] || 'Practice';
}

const getDifficultyFromLevel = (level) => {
    switch (level) {
        case 1: return 'Easy';
        case 2: return 'Medium';
        case 3: return 'Hard';
        default: return 'Medium';
    }
};

export const ExamView = ({ examId, user, onToggleBookmark, onNavigateToSignIn }) => {
    const [allQuestions, setAllQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filter states
    const [activeSubject, setActiveSubject] = useState('All');
    const [activeChapter, setActiveChapter] = useState('All');
    const [activeDifficulty, setActiveDifficulty] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [showAllFilters, setShowAllFilters] = useState(false);

    useEffect(() => {
        const fetchQuestions = async () => {
            setIsLoading(true);
            setError(null);
            setAllQuestions([]);
            clearFilters();
            setCurrentPage(1);

            try {
                let fetchedQuestions = [];
                // POINT BITSAT AND VITEEE TO THE CORRECT TABLES
                let tableName = examId.replace(/-/g, '');
                if (examId === 'bitsat') tableName = 'bitsat_questions';
                if (examId === 'viteee') tableName = 'vitee';
                
                console.log(`Fetching from table: ${tableName}`);
                const { data, error: dbError } = await supabase.from(tableName).select('*');
                if (dbError) throw dbError;

                if (data) {
                    fetchedQuestions = data.map((q) => ({
                        id: q.id.toString(),
                        text: q.question,
                        options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
                        answer: examId === 'viteee' ? q.correct_option : q.correct,
                        solution: examId === 'viteee' ? q.solution : q.explanation,
                        subject: q.subject || 'General',
                        topic: (examId === 'viteee' ? q.chapter : q.tags) || 'Unknown Topic',
                        difficulty: getDifficultyFromLevel(q.level),
                        type: q.type || 'Single Choice',
                    }));
                }
                
                setAllQuestions(fetchedQuestions);
            } catch (err) {
                console.error('Error fetching questions:', err);
                setError('Failed to load questions. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, [examId]);
    
    const clearFilters = () => {
        setActiveSubject('All');
        setActiveChapter('All');
        setActiveDifficulty('All');
        setSearchQuery('');
    };

    const subjects = useMemo(() => ['All', ...Array.from(new Set(allQuestions.map(q => q.subject)))], [allQuestions]);
    
    const chapters = useMemo(() => {
        const relevantQuestions = activeSubject === 'All' 
            ? allQuestions 
            : allQuestions.filter(q => q.subject === activeSubject);
        return ['All', ...Array.from(new Set(relevantQuestions.map(q => q.topic)))].sort();
    }, [allQuestions, activeSubject]);

    const filteredQuestions = useMemo(() => {
        return allQuestions.filter(q => {
            const subjectMatch = activeSubject === 'All' || q.subject === activeSubject;
            const chapterMatch = activeChapter === 'All' || q.topic === activeChapter;
            const difficultyMatch = activeDifficulty === 'All' || q.difficulty === activeDifficulty;
            const searchMatch = q.text.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                q.topic.toLowerCase().includes(searchQuery.toLowerCase());
            return subjectMatch && chapterMatch && difficultyMatch && searchMatch;
        });
    }, [allQuestions, activeSubject, activeChapter, activeDifficulty, searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeSubject, activeChapter, activeDifficulty, searchQuery]);

    const totalPages = Math.ceil(filteredQuestions.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const questionsForCurrentPage = filteredQuestions.slice(startIndex, startIndex + PAGE_SIZE);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const examName = getExamName(examId);

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-250px)]">
               <div className="flex flex-col items-center">
                 <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                 <p className="mt-4 text-brand-text-secondary animate-pulse">Fetching {examName} questions...</p>
               </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="text-center py-16 bg-brand-surface rounded-2xl border border-red-100 dark:border-red-900/30 shadow-sm">
                <div className="bg-red-50 dark:bg-red-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XIcon className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-brand-text-primary">Oops! Something went wrong</h3>
                <p className="text-brand-text-secondary mt-2 max-w-md mx-auto">{error}</p>
                <button onClick={() => window.location.reload()} className="mt-6 px-6 py-2 bg-brand-primary text-white rounded-lg font-semibold hover:bg-brand-primary-hover transition-colors">
                    Try Again
                </button>
            </div>
        );
    }

    const hasActiveFilters = activeSubject !== 'All' || activeChapter !== 'All' || activeDifficulty !== 'All' || searchQuery !== '';

    return (
        <div className="animate-fade-in space-y-8 pb-12">
            <header className="relative py-8 px-6 bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 rounded-3xl overflow-hidden border border-brand-border">
                <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                    <h1 className="text-3xl md:text-5xl font-black text-brand-text-primary italic uppercase tracking-tighter">
                        {examName} <span className="text-brand-primary">Practice Lab</span>
                    </h1>
                    <p className="mt-3 text-lg text-brand-text-secondary max-w-2xl">
                        Master the syllabus with targeted practice. Use the advanced filters below to customize your session.
                    </p>
                </div>
            </header>

            {/* Premium Filter Section */}
            <div className="sticky top-[63px] z-30 -mt-4 py-4">
                <div className="bg-brand-bg/80 backdrop-blur-xl border border-brand-border rounded-2xl shadow-xl shadow-black/5 p-4 space-y-4">
                    {/* Top Row: Search and Quick Toggles */}
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <div className="relative flex-grow w-full">
                            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-text-secondary" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by topic, keyword, or QID..."
                                className="w-full bg-brand-surface/50 border border-brand-border rounded-xl pl-12 pr-4 py-3 text-brand-text-primary placeholder-brand-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-text-secondary hover:text-brand-primary">
                                    <XIcon className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        
                        <div className="flex items-center space-x-2 w-full md:w-auto">
                            <button 
                                onClick={() => setShowAllFilters(!showAllFilters)}
                                className={`flex items-center justify-center space-x-2 px-5 py-3 rounded-xl border font-semibold text-sm transition-all flex-grow md:flex-grow-0 ${
                                    showAllFilters 
                                    ? 'bg-brand-primary text-white border-brand-primary shadow-lg shadow-brand-primary/20' 
                                    : 'bg-brand-surface border-brand-border text-brand-text-primary hover:border-brand-primary/50'
                                }`}
                            >
                                <SlidersIcon className="w-4 h-4" />
                                <span>{showAllFilters ? 'Hide Filters' : 'Show Filters'}</span>
                                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showAllFilters ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {hasActiveFilters && (
                                <button 
                                    onClick={clearFilters}
                                    className="p-3 text-brand-accent-red hover:bg-brand-accent-red/10 rounded-xl transition-colors shrink-0"
                                    title="Reset All Filters"
                                >
                                    <XIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Detailed Filters Group */}
                    {showAllFilters && (
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-6 animate-slide-down">
                            {/* Subject Filter */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-brand-text-secondary px-1">Subject</label>
                                <div className="flex flex-wrap gap-2">
                                    {subjects.map(subject => (
                                         <button
                                            key={subject}
                                            onClick={() => { setActiveSubject(subject); setActiveChapter('All'); }}
                                            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all border ${
                                                activeSubject === subject
                                                    ? 'bg-brand-primary border-brand-primary text-white shadow-md shadow-brand-primary/20'
                                                    : 'bg-brand-surface border-brand-border text-brand-text-secondary hover:border-brand-primary/30'
                                            }`}
                                        >
                                            {subject}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Chapter Filter */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-brand-text-secondary px-1">Chapter / Topic</label>
                                <div className="relative">
                                    <select 
                                        value={activeChapter}
                                        onChange={(e) => setActiveChapter(e.target.value)}
                                        className="w-full bg-brand-surface border border-brand-border rounded-lg px-3 py-2 text-sm text-brand-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                    >
                                        {chapters.map(chapter => (
                                            <option key={chapter} value={chapter}>{chapter}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Difficulty Filter */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-brand-text-secondary px-1">Difficulty</label>
                                <div className="flex items-center bg-brand-surface border border-brand-border rounded-lg p-1">
                                    {['All', 'Easy', 'Medium', 'Hard'].map(difficulty => (
                                        <button
                                            key={difficulty}
                                            onClick={() => setActiveDifficulty(difficulty)}
                                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                                                activeDifficulty === difficulty
                                                    ? 'bg-brand-bg text-brand-primary shadow-sm'
                                                    : 'text-brand-text-secondary hover:text-brand-text-primary'
                                            }`}
                                        >
                                            {difficulty}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Active Tabs / Quick Stats Bar */}
                {hasActiveFilters && (
                    <div className="mt-3 flex items-center px-4 space-x-2 overflow-x-auto no-scrollbar pb-1">
                        <span className="text-xs font-bold text-brand-text-secondary shrink-0">Found {filteredQuestions.length} questions matching:</span>
                        {activeSubject !== 'All' && <span className="shrink-0 px-3 py-1 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 rounded-full text-[10px] font-bold uppercase">{activeSubject}</span>}
                        {activeChapter !== 'All' && <span className="shrink-0 px-3 py-1 bg-brand-secondary/10 text-brand-secondary border border-brand-secondary/20 rounded-full text-[10px] font-bold uppercase">{activeChapter}</span>}
                        {activeDifficulty !== 'All' && <span className="shrink-0 px-3 py-1 bg-brand-accent-amber/10 text-brand-accent-amber border border-brand-accent-amber/20 rounded-full text-[10px] font-bold uppercase">{activeDifficulty}</span>}
                        {searchQuery && <span className="shrink-0 px-3 py-1 bg-brand-accent-violet/10 text-brand-accent-violet border border-brand-accent-violet/20 rounded-full text-[10px] font-bold uppercase">"{searchQuery}"</span>}
                    </div>
                )}
            </div>

            {/* Question List */}
            {filteredQuestions.length > 0 ? (
                 <div className="space-y-8 animate-slide-up">
                    <div className="grid gap-6">
                        {questionsForCurrentPage.map(q => {
                            const isBookmarked = !!user?.bookmarks?.questions?.[q.id];
                            return <QuestionCard key={q.id} question={q} user={user} isBookmarked={isBookmarked} onToggleBookmark={onToggleBookmark} onNavigateToSignIn={onNavigateToSignIn} />;
                        })}
                    </div>
                    
                    {totalPages > 1 && (
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-12 bg-brand-surface/50 border border-brand-border rounded-2xl p-4">
                            <button
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                className="w-full md:w-auto px-6 py-2.5 text-sm font-bold rounded-xl border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                Previous
                            </button>
                            <div className="flex items-center space-x-1">
                                <span className="px-3 py-1 bg-brand-primary text-white rounded-lg font-bold text-sm">{currentPage}</span>
                                <span className="text-brand-text-secondary font-medium mx-2">of</span>
                                <span className="px-3 py-1 bg-brand-surface border border-brand-border rounded-lg font-bold text-sm">{totalPages}</span>
                            </div>
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className="w-full md:w-auto px-6 py-2.5 text-sm font-bold rounded-xl border border-brand-border bg-brand-surface hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-20 bg-brand-surface/50 rounded-3xl border-2 border-dashed border-brand-border animate-fade-in group">
                    <div className="bg-brand-surface w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 transform group-hover:rotate-12 transition-transform">
                        <BookOpenIcon className="w-10 h-10 text-brand-text-secondary" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-text-primary">No matching questions</h3>
                    <p className="text-brand-text-secondary mt-2 max-w-sm mx-auto">
                       {allQuestions.length > 0
                         ? `We couldn't find any questions matching your current filters. Try resetting or selecting a different subject.`
                         : `Our question bank for ${examName} is currently empty. Please check back soon!`
                       }
                    </p>
                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="mt-8 px-8 py-3 bg-brand-primary text-white rounded-xl font-bold hover:shadow-lg hover:shadow-brand-primary/20 transition-all">
                            Reset Filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};